import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Mail } from 'lucide-react';
import { forgotPasswordSchema, type ForgotPasswordInput } from '@tutor/validation';
import type { ForgotPasswordResponse } from '@tutor/shared';
import { Alert, Button, Input } from '@tutor/ui';
import { api } from '../../lib/api-client';

export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const [devToken, setDevToken] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    // The endpoint always reports success, so there is no failure branch to show —
    // that is deliberate: it must not reveal whether an account exists.
    const response = await api.post<ForgotPasswordResponse>('/api/auth/forgot-password', values);
    setDevToken(response.devResetToken ?? null);
    setSent(true);
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-12">
      <div className="w-full max-w-sm">
        <Link
          to="/login"
          className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back to sign in
        </Link>

        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Reset your password</h1>

        {sent ? (
          <div className="mt-6 space-y-4">
            <Alert tone="success" title="Check your inbox">
              If an account exists for that address, we have sent a link to reset your password.
              The link expires in one hour.
            </Alert>

            {devToken && (
              <Alert tone="info" title="Development only">
                <p>Email delivery is not configured, so here is the reset link directly:</p>
                <Link
                  to={`/reset-password?token=${devToken}`}
                  className="mt-2 block break-all font-mono text-xs text-brand-700 underline"
                >
                  /reset-password?token={devToken}
                </Link>
              </Alert>
            )}
          </div>
        ) : (
          <>
            <p className="mt-1.5 text-sm text-slate-500">
              Enter the email address you sign in with and we will send you a reset link.
            </p>

            <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
              <Input
                label="Email address"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                leftIcon={<Mail className="h-4 w-4" aria-hidden />}
                error={errors.email?.message}
                {...register('email')}
              />

              <Button type="submit" fullWidth size="lg" isLoading={isSubmitting}>
                Send reset link
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
